import { AuthContext, useIsHydrated } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, ContainerDefault, ErrorMessage, ErrorMessagePage, Heading, Loader } from '@core/ui';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// One domain per line in the textarea <-> string[] on the wire. Normalization (lower-case, punycode,
// validation) is the grain's job: what comes back after save is what is shown.
const toLines = (domains: string[]) => domains.join('\n');
const toDomains = (text: string) =>
    text
        .replace(/\r/g, '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

export const ManageChatLinkDomainsPage = () => {
    const canManageChatLinkDomains = useContext(AuthContext).canManageChatLinkDomains;
    const { t } = useTranslation();
    const isHydrated = useIsHydrated();
    const queryClient = useQueryClient();

    const settingsQuery = OptionsApi.chatLinkSecurity.get();
    const { data: settings, isLoading, error } = useQuery(settingsQuery);
    const updateMutation = useMutation(OptionsApi.chatLinkSecurity.update());

    const [allowed, setAllowed] = useState('');
    const [blocked, setBlocked] = useState('');
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (settings) {
            setAllowed(toLines(settings.allowedDomains));
            setBlocked(toLines(settings.blockedDomains));
        }
    }, [settings]);

    if (!canManageChatLinkDomains) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const save = () => {
        setSaved(false);
        updateMutation.mutate(
            { allowedDomains: toDomains(allowed), blockedDomains: toDomains(blocked) },
            {
                onSuccess: (result) => {
                    setAllowed(toLines(result.allowedDomains));
                    setBlocked(toLines(result.blockedDomains));
                    setSaved(true);
                    queryClient.invalidateQueries({ queryKey: settingsQuery.queryKey });
                }
            }
        );
    };

    return (
        <ContainerDefault>
            <BackLink to="/manage">{t('Button.BackToManageEnv')}</BackLink>
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('ChatLinkDomains.PageHeading')}
            </Heading>
            <p className="max-w-4xl mb-8">{t('ChatLinkDomains.Intro')}</p>

            {error ? (
                <ErrorMessage apiError={error} classNames="mb-4 max-w-4xl" />
            ) : isLoading ? (
                <Loader />
            ) : (
                <>
                    {updateMutation.error && (
                        <div data-testid="chat-link-domains-error" className="mb-4 max-w-4xl">
                            <p className="font-bold text-warning">{t('ChatLinkDomains.Save.Error')}</p>
                            {/* statusMessage bypasses ErrorMessage's errorCode -> locale lookup (there is no key for
                                ValidationException) and shows the server's message, which names the offending entry. */}
                            <ErrorMessage apiError={updateMutation.error} statusMessage={updateMutation.error.message || undefined} />
                        </div>
                    )}
                    {saved && (
                        <p data-testid="chat-link-domains-saved" role="status" className="text-success mb-4">
                            {t('ChatLinkDomains.Save.Success')}
                        </p>
                    )}

                    <div className="flex flex-col md:flex-row items-start gap-4 max-w-4xl">
                        <div className="w-full bg-white rounded-lg p-4">
                            <label htmlFor="chat-link-domains-allowed" className="block font-accent text-2xl mb-1">
                                {t('ChatLinkDomains.AllowedDomains.Label')}
                            </label>
                            <p className="text-sm mb-2">{t('ChatLinkDomains.AllowedDomains.Help')}</p>
                            <div className="group relative w-full">
                                <textarea
                                    id="chat-link-domains-allowed"
                                    data-testid="chat-link-domains-allowed"
                                    className="w-full min-h-40 bg-transparent outline-hidden py-input-vert text-base"
                                    value={allowed}
                                    onChange={(e) => setAllowed(e.target.value)}
                                />
                                <div className="bg-black group-hover:bg-primary group-focus-within:bg-primary input-border"></div>
                            </div>
                        </div>
                        <div className="w-full bg-white rounded-lg p-4">
                            <label htmlFor="chat-link-domains-blocked" className="block font-accent text-2xl mb-1">
                                {t('ChatLinkDomains.BlockedDomains.Label')}
                            </label>
                            <p className="text-sm mb-2">{t('ChatLinkDomains.BlockedDomains.Help')}</p>
                            <div className="group relative w-full">
                                <textarea
                                    id="chat-link-domains-blocked"
                                    data-testid="chat-link-domains-blocked"
                                    className="w-full min-h-40 bg-transparent outline-hidden py-input-vert text-base"
                                    value={blocked}
                                    onChange={(e) => setBlocked(e.target.value)}
                                />
                                <div className="bg-black group-hover:bg-primary group-focus-within:bg-primary input-border"></div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6">
                        <Button
                            data-testid="chat-link-domains-save"
                            variant="primary"
                            onClick={save}
                            disabled={!isHydrated}
                            isLoading={updateMutation.isPending}
                        >
                            {t('ChatLinkDomains.Save')}
                        </Button>
                    </div>
                </>
            )}
        </ContainerDefault>
    );
};
